import dayjs from 'dayjs';
import { fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { TimeZone } from '../../time/timezone.js';
import { VersionRange } from '../core/version-range.js';
import { SerializationContext, DataModelBuilder, SerializedData, isSameData } from '../dto/dto.js';
import { NotificationEvent, SyncEventSource } from '../sync-event.js';
import { CustomerProfile } from './customer-profile.js';

export class ClientDeletedEvent extends NotificationEvent {
  static readonly TYPE_ID: string = 'de1e5b7f906843468941dd437b5df7d6';
  static readonly SCHEMA_VERSION: number = 2;

  constructor(
      public readonly uniqueId: string,
      public readonly creatorId: string | null,
      public readonly deviceId: string | null,
      public readonly creationDate: dayjs.Dayjs,
      public readonly source: SyncEventSource,
      public readonly seen: boolean,
      public readonly client: CustomerProfile,
      public readonly clientId: string,
  ) {
    super();
  }

  get $typeId(): string {
    return ClientDeletedEvent.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, ClientDeletedEvent.SCHEMA_VERSION);
  }

  static fromMap(context: SerializationContext, map: SerializedData): ClientDeletedEvent {
    return new ClientDeletedEvent(
        MapUtils.get<string>(map, 'uniqueId'),
        MapUtils.getOrNull<string>(map, 'creatorId'),
        MapUtils.getOrNull<string>(map, 'deviceId'),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        toEnumFromString(MapUtils.get<string>(map, 'source'), SyncEventSource),
        MapUtils.get<boolean>(map, 'seen'),
        context.fromData<CustomerProfile>(MapUtils.get<SerializedData>(map, 'client')),
        MapUtils.get<string>(map, 'clientId'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'creatorId': this.creatorId,
      'deviceId': this.deviceId,
      'creationDate': context.encodeDate(this.creationDate),
      'source': fromEnumAsString(this.source),
      'seen': this.seen,
      'client': context.toData(this.client),
      'clientId': this.clientId,
    };
  }

  clone(): this {
    return new ClientDeletedEvent(
        this.uniqueId,
        this.creatorId,
        this.deviceId,
        TimeZone.cloneDate(this.creationDate),
        this.source,
        this.seen,
        this.client.clone(),
        this.clientId,
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
      other.creatorId === this.creatorId &&
      other.deviceId === this.deviceId &&
      other.creationDate === this.creationDate &&
      other.source === this.source &&
      other.seen === this.seen &&
      isSameData(other.client, this.client) &&
      other.clientId === this.clientId;
  }

  markAsSeen(): this {
    return new ClientDeletedEvent(
        this.uniqueId,
        this.creatorId,
        this.deviceId,
        TimeZone.cloneDate(this.creationDate),
        this.source,
        true,
        this.client.clone(),
        this.clientId,
    ) as this;
  }
}

export class ClientDeletedEventBuilder extends DataModelBuilder<ClientDeletedEvent> {
  build(context: SerializationContext, map: SerializedData): ClientDeletedEvent {
    return ClientDeletedEvent.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(ClientDeletedEvent.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(ClientDeletedEvent.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    /**
     * Version 2:
     *  - add `deviceId` of type: string | null
     */
    if(version < 2) {
      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
